import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Redirect } from 'react-router-dom';

import {
  formatPrice,
} from 'common/formatters';

import {
  calculateHouseholdDepositFeesWithPaymentAccount,
} from 'common/householdHelpers';

import {
  hasRoleAnonymous,
} from 'common/userRoles';

import {
  PAYMENT_ACCOUNT_CARD,
  PAYMENT_ACCOUNT_BANK_ACCOUNT,
  PAYMENT_ACCOUNT_STATUS_PENDING_AUTOMATIC_VERIFICATION,
  PAYMENT_ACCOUNT_STATUS_PENDING_MANUAL_VERIFICATION,
} from 'common/paymentAccountConstants';

import {
  getListingManagedPhotoUrls,
  getMarketSecurityDepositLabel,
} from 'common/listingHelpers';

import {
  SR_API_ACTION_HOUSEHOLD_PAY_SECURITY_DEPOSIT,
  SR_API_ACTION_PLAID_CREATE_STRIPE_BANK_ACCOUNT,
  SR_API_ACTION_USER_CREATE_PAYMENT_ACCOUNT,
} from 'api/actionTypes';

import {
  SCREEN_ID_SECURITY_DEPOSIT_PAYMENT,
} from 'client/constants/screenIds';

import log from 'client/lib/log';
import branch from 'client/core/branch';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withApiState from 'client/helpers/withApiState';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import withLocationQuery from 'client/helpers/withLocationQuery';
import PartnerActions from 'client/actions/Partner';
import ListingActions from 'client/actions/Listing';
import MarketActions from 'client/actions/Market';
import PaymentAccountActions from 'client/actions/PaymentAccount';
import PlaidActions from 'client/actions/Plaid';
import getHouseholdDepositFeesAction from 'client/actions/Household/getHouseholdDepositFees';
import postWithState from 'client/actions/postWithState';

import SrBox from 'client/components/SrBox';
import SrPage from 'client/components/SrPage';
import SrBankAccountCard from 'client/components/SrBankAccountCard/SrBankAccountCard';
import ButtonDeprecated, {
  BUTTON_THEME_GREEN,
} from 'client/components/ButtonDeprecated';
import SrLink from 'client/components/SrLink/SrLink';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrChoosePaymentAccountModal from 'client/components/SrChoosePaymentAccountModal';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';

const debug = log.debug('sunroom:components:SecurityDepositPaymentPage');

const SrPriceBreakdownRow = ({ label, depositFees, feeId, rowClassName }) => (
  depositFees[feeId]
    ? (
      <span className={classnames('row', rowClassName)}>
        <span className='label'>{label}</span>
        <span className='amount'>{formatPrice(depositFees[feeId])}</span>
        <span className='dottedLine' />
      </span>
    )
    : null
);

SrPriceBreakdownRow.propTypes = {
  label: PropTypes.string,
  depositFees: PropTypes.object.isRequired,
  feeId: PropTypes.string.isRequired,
  rowClassName: PropTypes.string,
};

SrPriceBreakdownRow.displayName = 'SrPriceBreakdownRow';

@withTransitionHook
@withApiState({
  householdPaySecurityDepositApiState: {
    action: SR_API_ACTION_HOUSEHOLD_PAY_SECURITY_DEPOSIT,
  },
  userCreatePaymentAccountApiState: {
    action: SR_API_ACTION_USER_CREATE_PAYMENT_ACCOUNT,
  },
  plaidCreateStripeBankAccountApiState: {
    action: SR_API_ACTION_PLAID_CREATE_STRIPE_BANK_ACCOUNT,
  },
})
@branch(({ match }) => ({
  depositFees: ['household', 'depositFeesByListingId', match.params.listingId],
  currentUser: ['currentUser'],
  listing: ['listing'],
  plaidLinkToken: ['plaidLinkToken'],
  userAgent: ['analytics', 'userAgent'],
}))
@branch(({ currentUser, listing }) => {
  const cursors = {};

  if (currentUser?.id) {
    cursors.paymentAccounts = ['paymentAccounts', 'byUserId', currentUser.id];
  }

  if (listing) {
    cursors.partner = ['partners', 'byId', listing.partnerId];

    if (listing.marketId) {
      cursors.market = ['markets', 'byId', listing.marketId];
    }
  }

  return cursors;
})
@withScrollToTop
@withLocationQuery
@withScreenId(SCREEN_ID_SECURITY_DEPOSIT_PAYMENT)
class SecurityDepositPaymentPage extends Component {
  static fetchData(match, inject) {
    const householdId = parseInt(match.params.householdId, 10);
    const listingId = parseInt(match.params.listingId, 10);
    const userId = inject.state.get(['currentUser', 'id']);

    if (!userId) {
      return [];
    }

    return Promise.all([
      ListingActions.getById(inject, listingId),
      PaymentAccountActions.getByUserId(inject, { userId, refresh: true }),
      getHouseholdDepositFeesAction(inject, { householdId, listingId }),
      PlaidActions.getLinkToken(inject, { userId }),
    ]).then(() => {
      const promises = [];
      const listing = inject.state.get(['listing']);

      if (listing?.partnerId) {
        promises.push(PartnerActions.getById(inject, {
          partnerId: listing.partnerId,
        }));
      }

      if (listing?.marketId) {
        promises.push(MarketActions.getById(inject, { marketId: listing.marketId }));
      }

      return Promise.all(promises);
    });
  }

  static getPageMetadata = (state) => {
    const listing = state.get(['listing']);
    const market = listing?.marketId
      ? state.get(['markets', 'byId', listing.marketId])
      : null;

    return {
      pageTitle: `Pay ${getMarketSecurityDepositLabel(market)} | Sunroom`,
    };
  }

  static propTypes = {
    currentUser: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    depositFees: PropTypes.object,
    listing: PropTypes.object,
    householdPaySecurityDepositApiState: PropTypes.object.isRequired,
    userCreatePaymentAccountApiState: PropTypes.object.isRequired,
    plaidCreateStripeBankAccountApiState: PropTypes.object.isRequired,
    dispatch: PropTypes.func.isRequired,
    plaidLinkToken: PropTypes.string,
    userAgent: PropTypes.object.isRequired,
    locationQuery: PropTypes.object.isRequired,
  }

  static getDerivedStateFromProps(props, state) {
    if (
      !state.selectPaymentAccount
      && !state.changePaymentAccountType
      && !state.paymentAccount
      && props.partner
      && !props.partner.experienceConfiguration.achEnabled
    ) {
      const paymentAccount = props.paymentAccounts
        .find((paymentAccount) => paymentAccount.type === PAYMENT_ACCOUNT_CARD) || null;

      return { paymentAccount };
    }

    return null;
  }

  constructor(props) {
    super(props);
    this.state = {
      securityDepositPaid: false,
      showErrorMessage: false,
      selectPaymentAccount: false,
      changePaymentAccountType: null,
      paymentAccount: null,
    };
  }

  componentDidMount() {
    const { locationQuery, paymentAccounts } = this.props;

    if (locationQuery.pa) {
      const paymentAccountId = parseInt(locationQuery.pa, 10);
      const paymentAccount = (paymentAccounts || []).find(({ id }) => id === paymentAccountId);
      if (paymentAccount) {
        this._onPaymentAccount(paymentAccount);
      }
    }
  }

  _onSecurityDepositFormSubmit = ({ source, paymentAccount }) => {
    const {
      listing,
      depositFees,
      dispatch,
      match: {
        params: {
          householdId: householdIdString,
        },
      },
    } = this.props;

    const householdDepositFees = calculateHouseholdDepositFeesWithPaymentAccount({
      depositFees,
      paymentAccount,
    });

    const securityDepositApiPayload = {
      householdId: parseInt(householdIdString, 10),
      listingId: listing.id,
      amount: householdDepositFees.total,
    };

    dispatch(postWithState, {
      action: SR_API_ACTION_HOUSEHOLD_PAY_SECURITY_DEPOSIT,
      payload: {
        ...securityDepositApiPayload,
        source,
        paymentAccountId: paymentAccount?.id,
      },
      onSuccess: () => {
        this.setState({
          securityDepositPaid: true,
        });

        if (paymentAccount?.type === PAYMENT_ACCOUNT_BANK_ACCOUNT) {
          setTimeout(() => {
            if (!this._isUnmounting) {
              this.setState({ securityDepositPaid: false });
              this._onSecurityDepositPaidModalButtonClick();
            }
          }, 3000);
        }
      },
      onError: () => {
        this.setState({ showErrorMessage: true });
      },
    });
  }

  _onSecurityDepositPaidModalButtonClick = () => {
    const { currentUser, history } = this.props;
    if (hasRoleAnonymous(currentUser.roles)) {
      history.push('/feed');
    }
    else {
      history.push('/profile');
    }
  }

  _onSelectPaymentAccount = () => {
    this.setState({ selectPaymentAccount: true });
  }

  _onPaymentAccount = (paymentAccount) => {
    const { history } = this.props;
    debug('_onPaymentAccount', paymentAccount);
    if (
      paymentAccount.status === PAYMENT_ACCOUNT_STATUS_PENDING_AUTOMATIC_VERIFICATION
      || paymentAccount.status === PAYMENT_ACCOUNT_STATUS_PENDING_MANUAL_VERIFICATION
    ) {
      history.push('/profile');
    }
    else {
      this.setState({
        paymentAccount,
        selectPaymentAccount: false,
        changePaymentAccountType: null,
        showErrorMessage: false,
      });
    }
  }

  _onChangePaymentAccount = () => {
    const { paymentAccount } = this.state;
    this.setState({
      changePaymentAccountType: paymentAccount.type,
    });
  }

  _onPay = () => {
    const { paymentAccount } = this.state;

    this._onSecurityDepositFormSubmit({
      paymentAccount,
    });
  }

  _onPlaidLinkSuccess = (public_token, metadata) => {
    debug('_onPlaidLinkSuccess', { public_token, metadata });

    const {
      listing,
      dispatch,
    } = this.props;

    dispatch(postWithState, {
      action: SR_API_ACTION_PLAID_CREATE_STRIPE_BANK_ACCOUNT,
      payload: {
        publicToken: public_token,
        metadata,
        listingIdForNotification: listing.id,
      },
      onSuccess: this._onPaymentAccount,
      onError: () => {
        this.setState({
          selectPaymentAccount: false,
          changePaymentAccountType: null,
          showErrorMessage: true,
        });
      },
    });
  }

  _onPlaidLinkExit = (error, metadata) => {
    debug('_onPlaidLinkExit', { error, metadata });
  }

  _onCancelPaymentAccount = () => {
    const { history } = this.props;
    const { selectPaymentAccount, changePaymentAccountType } = this.state;

    if (changePaymentAccountType) {
      this.setState({ changePaymentAccountType: null });
    }
    else if (selectPaymentAccount) {
      this.setState({ selectPaymentAccount: false });
    }
    else {
      history.push('/profile');
    }
  }

  render() {
    const {
      listing,
      market,
      depositFees,
      householdPaySecurityDepositApiState,
      userCreatePaymentAccountApiState,
      plaidCreateStripeBankAccountApiState,
      plaidLinkToken,
      paymentAccounts,
      partner,
      isTransitioning,
      userAgent,
      match,
      currentUser,
    } = this.props;

    const {
      paymentAccount,
      securityDepositPaid,
      showErrorMessage,
      selectPaymentAccount,
      changePaymentAccountType,
    } = this.state;

    if (isTransitioning) {
      return null;
    }

    const householdDepositFees = paymentAccount ? calculateHouseholdDepositFeesWithPaymentAccount({
      depositFees,
      paymentAccount,
    }) : {};

    const managedPhotoUrls = getListingManagedPhotoUrls({ listing });
    const errorMessage = (
      plaidCreateStripeBankAccountApiState.error?.message
      || userCreatePaymentAccountApiState.error?.message
      || householdPaySecurityDepositApiState.error?.message
    );

    log.info('depositFees', depositFees);

    const navbarProps = {};

    if (hasRoleAnonymous(currentUser.roles)) {
      return (
        <Redirect to='/feed' />
      );
    }

    if (userAgent.isMobile) {
      navbarProps.leftButton = (
        <SrLink
          restoreScrollPosition
          to='/profile'
        >
          <span className='link back' />
        </SrLink>
      );
    }

    return (
      <div className='SecurityDepositPaymentPage'>
        <SrPage navbarProps={navbarProps}>
          <div className='SrPageContainer'>
            <div className='contentContainer'>
              {!listing || !depositFees ? <div className='loading-gif' /> : (
                <div className='mainCard'>
                  <div
                    className='listingPhoto'
                    style={{ backgroundImage: 'url(' + managedPhotoUrls[0] + ')' }}
                  />
                  <div className='header'>
                    <span className='headline'>Review & Pay</span>
                    <span className='listingAddress'>{listing.addressStreetDisplay}</span>
                    <div className='subhead'>
                      <span className='cityStateZip'>
                        {listing.addressCity}, {listing.addressStateCode} {listing.addressZip}
                      </span>
                      {listing.apartmentName && (
                        <span className='apartmentName'>{listing.apartmentName}</span>
                      )}
                    </div>
                  </div>
                  <div className='priceBreakdown'>
                    <SrPriceBreakdownRow
                      label='first month’s rent'
                      depositFees={depositFees}
                      feeId='firstMonthRent'
                    />
                    <SrPriceBreakdownRow
                      label='pet rent'
                      depositFees={depositFees}
                      feeId='monthlyPetRent'
                    />
                    <SrPriceBreakdownRow
                      label={getMarketSecurityDepositLabel(market)}
                      depositFees={depositFees}
                      feeId='securityDeposit'
                    />
                    <SrPriceBreakdownRow
                      label='pet deposit'
                      depositFees={depositFees}
                      feeId='petDeposit'
                    />
                    <SrPriceBreakdownRow
                      label='pet fees'
                      depositFees={depositFees}
                      feeId='petFees'
                    />
                    <SrPriceBreakdownRow
                      label='administration fee'
                      depositFees={depositFees}
                      feeId='adminFee'
                    />
                    {paymentAccount && (
                      <SrPriceBreakdownRow
                        label='convenience fee'
                        depositFees={householdDepositFees}
                        feeId='stripeFee'
                      />
                    )}
                    <SrPriceBreakdownRow
                      label='total'
                      depositFees={paymentAccount ? householdDepositFees : depositFees}
                      feeId={paymentAccount ? 'total' : 'totalWithoutStripeFees'}
                      rowClassName='total'
                    />
                  </div>
                  <>
                    {paymentAccount && (
                      <SrBankAccountCard
                        bankAccount={paymentAccount}
                        onChangeClick={this._onChangePaymentAccount}
                      />
                    )}
                    <SrBox marginTop='52px'>
                      {paymentAccount && (
                        <ButtonDeprecated
                          disabled={householdPaySecurityDepositApiState.inProgress}
                          type='button'
                          onClick={this._onPay}
                          theme={BUTTON_THEME_GREEN}
                        >
                          pay {formatPrice(householdDepositFees.total)}
                        </ButtonDeprecated>
                      )}
                      {!paymentAccount && (
                        <ButtonDeprecated
                          className='continue'
                          disabled={selectPaymentAccount}
                          type='button'
                          onClick={this._onSelectPaymentAccount}
                        >
                          continue
                        </ButtonDeprecated>
                      )}
                      {errorMessage && showErrorMessage && (
                        <SrBox marginTop='12px'>
                          <SrErrorMessage>
                            {errorMessage}
                          </SrErrorMessage>
                        </SrBox>
                      )}
                      <span className='helperTextContainer'>
                        <span className='helperText'>
                          <span role='img' aria-label='lock'> 🔒</span> Securely encrypted payment
                        </span>
                      </span>
                    </SrBox>
                  </>
                  <div className='stripePowered' />
                </div>
              )}
            </div>
          </div>
        </SrPage>
        {(selectPaymentAccount || changePaymentAccountType) && partner && depositFees && plaidLinkToken && (
          <SrChoosePaymentAccountModal
            stripeFee={depositFees.cardStripeFee}
            userCreatePaymentAccountInProgress={userCreatePaymentAccountApiState.inProgress}
            changePaymentAccountType={changePaymentAccountType}
            partner={partner}
            paymentAccounts={paymentAccounts}
            plaidLinkToken={plaidLinkToken}
            plaidLinkJSON={{
              token: plaidLinkToken,
              listingId: match.params.listingId,
              householdId: match.params.householdId,
            }}
            onPlaidLinkSuccess={this._onPlaidLinkSuccess}
            onPlaidLinkExit={this._onPlaidLinkExit}
            onPaymentAccount={this._onPaymentAccount}
            onCancel={this._onCancelPaymentAccount}
          />
        )}
        {securityDepositPaid && (
          <SrSimpleMessageModal
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            iconUrl='/images/checkmarkSuccessGreen.svg'
            headline={paymentAccount?.type === PAYMENT_ACCOUNT_BANK_ACCOUNT
              ? 'Payment Initiated!'
              : 'Successfully paid!'
            }
            message={paymentAccount?.type === PAYMENT_ACCOUNT_BANK_ACCOUNT ? (<SrBox height='160px' />) :
              ('Awesome, Sunroom will prepare the lease now. We\'ll email you instructions ' +
              'about how to sign the lease soon. Each applicant must sign! The landlord will ' +
              'sign afterwards, making the lease official.')
            }
            buttonText={paymentAccount?.type === PAYMENT_ACCOUNT_BANK_ACCOUNT ? '' : 'ok thanks'}
            maxWidth={400}
            maxIconWidth={200}
            onClose={this._onSecurityDepositPaidModalButtonClick}
          />
        )}
      </div>
    );
  }
}

SecurityDepositPaymentPage.displayName = 'SecurityDepositPaymentPage';

export default SecurityDepositPaymentPage;
